import React, { CSSProperties, FC, useCallback, useState, MouseEvent, KeyboardEvent, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { sounds, throttle } from '@whitesharx/starfall-frontend-ui-kit';
import { Adaptive } from './components/Adaptive';
import { Chess } from './components/Chess';

import {
  modalStyles,
  overlayStyles,
  overlayBackgroundStyles,
  modalWidthStyles,
  modalFullWindowStyles,
  borderStyles,
} from './styles.css';
import { isClient } from '~/src/constants/env';

export type ModalProps = {
  transparentOverlay?: boolean;
  autoWidth?: boolean;
  maxWidth?: number;
  fullWindow?: boolean;
  isOpen: boolean;
  className?: string;
  overlayClassName?: string;
  overlayStyle?: CSSProperties;
  onRequestClose?: (e: MouseEvent<HTMLDivElement> | KeyboardEvent) => void;
};

type ModalType = { Adaptive: FC<ModalProps>; Chess: typeof Chess };

const playSound = () => sounds.play('click');

const modalRoot = isClient && document.getElementById('modal-root');

const Modal: FC<ModalProps> & ModalType = ({
  transparentOverlay,
  maxWidth,
  autoWidth,
  children,
  fullWindow,
  isOpen,
  className,
  overlayClassName,
  onRequestClose,
}) => {
  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<string>('100vh');

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const isClickOutside = !(contentRef && contentRef.contains(e.target as Node));
    if (onRequestClose && isClickOutside) {
      playSound();
      onRequestClose(e);
    }
  };

  const onResize = useCallback(() => {
    if (fullWindow) {
      const actualHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      setMaxHeight(`${actualHeight}px`);
    }
  }, []);

  useEffect(() => {
    const throttledEffect = throttle(onResize, 50);

    throttledEffect();

    const view = window.visualViewport || window;

    view.addEventListener('resize', throttledEffect);

    return () => {
      view.removeEventListener('resize', throttledEffect);
    };
  }, [onResize]);

  if (!modalRoot || !isOpen) {
    return null;
  }

  return createPortal(
    <div
      style={{ display: contentRef ? 'unset' : 'none' }}
      className={classNames(
        overlayClassName,
        overlayStyles,
        transparentOverlay ? overlayBackgroundStyles.transparent : overlayBackgroundStyles.base
      )}
      onClick={handleOverlayClick}
    >
      <div
        ref={setContentRef}
        className={classNames(className, modalStyles, {
          [modalWidthStyles]: !(autoWidth || fullWindow),
          [modalFullWindowStyles]: fullWindow,
          [borderStyles]: !fullWindow,
        })}
        style={{ maxWidth, maxHeight }}
      >
        {children}
      </div>
    </div>,
    modalRoot
  );
};

Modal.Adaptive = Adaptive;
Modal.Chess = Chess;

export default Modal;
